import { useCallback, useState } from "react";
import { useAgentSessionContext } from "@/components/chat/AgentSessionContext";
import {
	getActiveAgentJob,
	listChatMessages,
	loadChat,
	resolveToolApproval,
	ToolPolicyConflictError,
	type ToolApprovalDecision,
} from "@/lib/api";
import { useChatStore } from "@/stores/useChatStore";
import { useToastStore } from "@/stores/useToastStore";
import { findPendingHITL } from "@/components/hitl/hitl";

/** 承認 API の結果を送信し、競合時を含め履歴から HITL 状態を復元する。 */
export function useResolveToolApproval() {
	const { startStreaming, stopStreaming } = useAgentSessionContext();
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const resync = useCallback(async (chatId: string) => {
		const [messages, metadata, activeJob] = await Promise.all([
			listChatMessages(chatId),
			loadChat(chatId),
			getActiveAgentJob(chatId),
		]);
		const store = useChatStore.getState();
		if (store.chatId !== chatId) return;
		store.setMessages(messages);
		if (metadata !== null) store.setChatMetadata(metadata);
		store.setActiveAgentJob(activeJob?.id ?? null);
		const pending = findPendingHITL(messages);
		if (pending === null) store.clearHITL();
		else store.setHITL(pending);
	}, []);

	const submit = useCallback(
		async (decision: ToolApprovalDecision) => {
			const { chatId, hitlState, status } = useChatStore.getState();
			if (!chatId || hitlState?.kind !== "tool-approval" || status === "stopping" || submitting)
				return;

			setSubmitting(true);
			setError(null);
			let decided = false;
			try {
				await resolveToolApproval(chatId, hitlState.toolUseId, decision);
				decided = true;
				// 旧ストリームが resync の GET 群より先に新しい承認プロンプト等を配信すると、
				// resync の古いスナップショットがそれを消してしまう。先に止めて競合を断つ。
				stopStreaming();
				await resync(chatId);
				startStreaming(chatId);
			} catch (caught: unknown) {
				if (caught instanceof ToolPolicyConflictError) {
					try {
						await resync(chatId);
					} catch (syncError) {
						console.error("承認状態の再取得に失敗しました", syncError);
					}
				} else if (decided) {
					// 決定はサーバーに記録済みで resync だけが失敗した。決定済みカードを操作可能な
					// まま残さないようクリアし、ストリーム再開で以降の状態配信に委ねる。
					if (useChatStore.getState().chatId === chatId) {
						useChatStore.getState().clearHITL();
					}
					startStreaming(chatId);
				}
				const message =
					caught instanceof Error
						? caught.message
						: "承認結果を送信できませんでした。しばらくしてから再度お試しください。";
				setError(message);
				useToastStore.getState().show({ type: "error", message });
				throw caught;
			} finally {
				setSubmitting(false);
			}
		},
		[resync, startStreaming, stopStreaming, submitting],
	);

	return { submit, submitting, error };
}
